import { useMemo } from "react";
import { buildDeviceInfo } from "../utilities/tachographDeviceInfo";
import { useDiagnosticsBySearch } from "@/hooks/diagnostic/useDiagnosticsBySearch";
import { useTachographDeviceStatusData } from "@/hooks/tachograph/useTachographDeviceStatusData";

const DIAGNOSTIC_LIMIT = 500;

/**
 * The GO device's own tachograph readings. Fixed ids rather than a name search: MyGeotab names
 * them in the user's language ("Funktionen des Fahrtenschreibers: …"), so an English name pattern
 * found nothing for a German user and the make, calibration and RDL columns stayed empty.
 */
export const TACHOGRAPH_DIAGNOSTIC_IDS = {
  manufacturerId: "ackm3tWUgMkWYVqKl3b3hyg",
  calibrationId: "agOXwO-vH0EW66Ry_cjkJrg",
  remoteDownloadId: "aBKJtgMu9GUyy9QP-hwjnHQ",
  downloadTaskId: "a8swsqAVh8UOxRe3NieOPSA",
  tachographStatusId: "adyiiM8vk-0qo4JrOPe4n2A",
};

const { tachographStatusId, ...SLOW_IDS } = TACHOGRAPH_DIAGNOSTIC_IDS;

const WANTED = Object.values(SLOW_IDS);

/** The tachograph status is sent hundreds of times a month; two days of it say what it is now. */
const FREQUENT = [tachographStatusId];
const FREQUENT_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * What each vehicle's tachograph says about itself: its make, its next calibration, whether
 * remote download is on and the raw download task and tachograph status.
 * `deviceIds` are the vehicles in the group filter; readings of other vehicles are not loaded.
 */
export function useTachographDeviceInfo(deviceIds: string[]) {
  // Only asked whether this database knows tachographs at all, for the connector tab's hint.
  const {
    data: diagnostics = [],
    isLoading: isLoadingDiagnostics,
    isError: isErrorDiagnostics,
  } = useDiagnosticsBySearch("Tachograph", false, DIAGNOSTIC_LIMIT);

  const {
    data: statusData = [],
    isLoading: isLoadingStatusData,
    isError: isErrorStatusData,
  } = useTachographDeviceStatusData(WANTED, deviceIds);
  const {
    data: frequentStatusData = [],
    isLoading: isLoadingFrequent,
    isError: isErrorFrequent,
  } = useTachographDeviceStatusData(FREQUENT, deviceIds, FREQUENT_WINDOW_MS);

  const data = useMemo(
    () => buildDeviceInfo([...statusData, ...frequentStatusData], TACHOGRAPH_DIAGNOSTIC_IDS),
    [statusData, frequentStatusData],
  );

  return {
    data,
    /** False while loading (including before the vehicle list has arrived) or after a failure. */
    canShowMissing:
      deviceIds.length > 0 && !isLoadingStatusData && !isErrorStatusData && !isLoadingFrequent && !isErrorFrequent,
    /** True once the search loaded and this database has no tachograph diagnostic at all. */
    diagnosticsMissing:
      !isLoadingDiagnostics &&
      !isErrorDiagnostics &&
      diagnostics.length === 0,
    isLoading: isLoadingDiagnostics || isLoadingStatusData || isLoadingFrequent,
    isError: isErrorDiagnostics || isErrorStatusData || isErrorFrequent,
  };
}
